/**
 * Help / FAQ copy builders driven by FinancialConfig Age Pension schedules.
 * Keeps HelpCenter and HelpSystem in sync with Services Australia indexation switches.
 */

import { agePensionCoreAsAt, deemingAsAt } from '../FinancialConfig.js';

const fmtMoney = (n, decimals = 0) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0';
  return `$${v.toLocaleString('en-AU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

const pct = (rate) => `${(rate * 100).toFixed(2).replace(/\.?0+$/, '')}%`;

/** Live Age Pension facts for help surfaces (totals include supplements). */
export function getAgePensionHelpFacts(asAt = new Date()) {
  const core = agePensionCoreAsAt(asAt);
  const deeming = deemingAsAt(asAt);
  const sepSwitch = new Date('2026-09-20T00:00:00+10:00');
  const fromSep = asAt >= sepSwitch;

  const singleTotal =
    core.MAX_PENSION.SINGLE +
    core.SUPPLEMENTS.PENSION_SUPPLEMENT.SINGLE +
    core.SUPPLEMENTS.ENERGY_SUPPLEMENT.SINGLE;
  const coupleTotal =
    core.MAX_PENSION.COUPLE +
    core.SUPPLEMENTS.PENSION_SUPPLEMENT.COUPLE +
    core.SUPPLEMENTS.ENERGY_SUPPLEMENT.COUPLE;

  const singleFn = singleTotal / 26;
  const coupleFn = coupleTotal / 26;

  const at = core.ASSET_TEST;
  const nonHomeownerExtra =
    at.SINGLE_NON_HOMEOWNER.FREE_THRESHOLD - at.SINGLE_HOMEOWNER.FREE_THRESHOLD;

  const craSingleFn = core.RENT_ASSISTANCE.SINGLE.MAX_PAYMENT / 26;
  const craCoupleFn = core.RENT_ASSISTANCE.COUPLE.MAX_PAYMENT / 26;
  const craMinSingleFn = core.RENT_ASSISTANCE.SINGLE.MIN_RENT / 26;
  const craMinCoupleFn = core.RENT_ASSISTANCE.COUPLE.MIN_RENT / 26;

  return {
    asAt,
    fromSep,
    ratePeriodLabel: fromSep
      ? 'from 20 September 2026'
      : 'from 20 March 2026 (until 20 September indexation)',
    assetEffectiveLabel: 'from 1 July 2026',
    singleTotalAnnual: singleTotal,
    coupleTotalAnnual: coupleTotal,
    singleTotalFn: singleFn,
    coupleTotalFn: coupleFn,
    assetTest: at,
    incomeTest: core.INCOME_TEST,
    deeming,
    nonHomeownerExtra,
    cra: {
      singleMaxFn: craSingleFn,
      coupleMaxFn: craCoupleFn,
      singleMinRentFn: craMinSingleFn,
      coupleMinRentFn: craMinCoupleFn,
      taperRate: core.RENT_ASSISTANCE.SINGLE.TAPER_RATE,
    },
  };
}

export function agePensionMaxFaqAnswer(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  return (
    `Maximum pension (${f.ratePeriodLabel}): Singles about ${fmtMoney(f.singleTotalAnnual)}/year ` +
    `(${fmtMoney(f.singleTotalFn, 2)}/fortnight), Couples about ${fmtMoney(f.coupleTotalAnnual)}/year combined ` +
    `(${fmtMoney(f.coupleTotalFn, 2)}/fortnight). This includes pension and energy supplements. ` +
    `Asset free areas rose on 1 July 2026 (e.g. single homeowner ${fmtMoney(f.assetTest.SINGLE_HOMEOWNER.FREE_THRESHOLD)}). ` +
    `If renting, add Rent Assistance. You must be 67+ and pass the asset and income tests. ` +
    `Many people with significant super still qualify for a part-pension.`
  );
}

export function agePensionAssetFaqAnswer(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  const at = f.assetTest;
  return (
    `Homeowners (${f.assetEffectiveLabel}): Singles get full pension up to ${fmtMoney(at.SINGLE_HOMEOWNER.FREE_THRESHOLD)} assets ` +
    `(cuts off at ${fmtMoney(at.SINGLE_HOMEOWNER.CUT_OFF)}), Couples up to ${fmtMoney(at.COUPLE_HOMEOWNER.FREE_THRESHOLD)} ` +
    `(cuts off at ${fmtMoney(at.COUPLE_HOMEOWNER.CUT_OFF)}). Non-homeowners: Singles up to ${fmtMoney(at.SINGLE_NON_HOMEOWNER.FREE_THRESHOLD)} ` +
    `(cuts off at ${fmtMoney(at.SINGLE_NON_HOMEOWNER.CUT_OFF)}), Couples up to ${fmtMoney(at.COUPLE_NON_HOMEOWNER.FREE_THRESHOLD)} ` +
    `(cuts off at ${fmtMoney(at.COUPLE_NON_HOMEOWNER.CUT_OFF)}). Your home is exempt. Assets reduce pension by $3/fortnight per $1,000 over the threshold. ` +
    `The lower result of asset or income test applies.`
  );
}

export function agePensionDeemingFaqAnswer(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  const d = f.deeming;
  return (
    `Deeming assumes your financial assets (super, savings, investments) earn fixed rates regardless of actual returns: ` +
    `${pct(d.LOW_RATE)} on first ${fmtMoney(d.LOW_THRESHOLD.SINGLE)} (singles) or ${fmtMoney(d.LOW_THRESHOLD.COUPLE)} (couples), ` +
    `then ${pct(d.HIGH_RATE)} above that. Your home is not deemed. This deemed income is used for the income test. ` +
    `During market downturns, deeming can work in your favour by assuming lower earnings than actual.`
  );
}

export function rentAssistanceFaqAnswer(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  const { cra } = f;
  return (
    `Rent Assistance provides up to ${fmtMoney(cra.singleMaxFn, 2)}/fortnight for singles ` +
    `(${fmtMoney(cra.coupleMaxFn, 2)} for couples combined) to help with rent costs. ` +
    `You must be receiving Age Pension (even partial), paying at least ${fmtMoney(cra.singleMinRentFn, 0)}/fortnight rent (singles) ` +
    `or ${fmtMoney(cra.coupleMinRentFn, 0)}/fortnight (couples), and be a non-homeowner. ` +
    `Interestingly, singles receive MORE than couples combined, reflecting the policy assumption that couples share rent costs more efficiently.`
  );
}

export function renterRetirementFaqAnswer(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  return (
    `Renters typically need 20-30% more retirement income than homeowners to cover rent. However, you get two benefits: ` +
    `(1) Commonwealth Rent Assistance of up to ${fmtMoney(f.cra.singleMaxFn, 2)}/fortnight if receiving Age Pension, and ` +
    `(2) ${fmtMoney(f.nonHomeownerExtra)} higher asset test threshold for Age Pension eligibility. ` +
    `Factor rent increases into your planning - rent grows faster than general inflation.`
  );
}

/** HTML block for HelpSystem Age Pension tab. */
export function agePensionHelpHtml(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  const at = f.assetTest;
  const it = f.incomeTest;
  const d = f.deeming;
  return `
        <h3>Age Pension Integration</h3>
        <p>The Age Pension can provide significant income in retirement. Understanding how it works is crucial.</p>
        
        <h4>Eligibility:</h4>
        <ul>
          <li><strong>Age requirement</strong> - 67 years old (for those born after 1957)</li>
          <li><strong>Residency</strong> - 10+ years Australian residence</li>
          <li><strong>Means tested</strong> - Subject to asset test and income test</li>
        </ul>
        
        <h4>Maximum Pension Rates (${f.ratePeriodLabel}):</h4>
        <ul>
          <li><strong>Single</strong> - about ${fmtMoney(f.singleTotalAnnual)}/year (${fmtMoney(f.singleTotalFn, 2)}/fortnight)</li>
          <li><strong>Couple combined</strong> - about ${fmtMoney(f.coupleTotalAnnual)}/year (${fmtMoney(f.coupleTotalFn, 2)}/fortnight)</li>
          <li><strong>Plus supplements</strong> - Energy and pension supplements included in the totals above</li>
          <li><strong>Plus rent assistance</strong> - If renting (CRA indexed separately)</li>
        </ul>
        
        <h4>Asset Test (${f.assetEffectiveLabel}):</h4>
        <ul>
          <li><strong>Single homeowner</strong> - Full pension up to ${fmtMoney(at.SINGLE_HOMEOWNER.FREE_THRESHOLD)}, cuts off at ${fmtMoney(at.SINGLE_HOMEOWNER.CUT_OFF)}</li>
          <li><strong>Couple homeowner</strong> - Full pension up to ${fmtMoney(at.COUPLE_HOMEOWNER.FREE_THRESHOLD)}, cuts off at ${fmtMoney(at.COUPLE_HOMEOWNER.CUT_OFF)}</li>
          <li><strong>Single non-homeowner</strong> - Full pension up to ${fmtMoney(at.SINGLE_NON_HOMEOWNER.FREE_THRESHOLD)}, cuts off at ${fmtMoney(at.SINGLE_NON_HOMEOWNER.CUT_OFF)}</li>
          <li><strong>Couple non-homeowner</strong> - Full pension up to ${fmtMoney(at.COUPLE_NON_HOMEOWNER.FREE_THRESHOLD)}, cuts off at ${fmtMoney(at.COUPLE_NON_HOMEOWNER.CUT_OFF)}</li>
          <li><strong>Taper rate</strong> - Reduces by $3/fortnight for every $1,000 over threshold</li>
        </ul>
        
        <h4>Income Test:</h4>
        <ul>
          <li><strong>Single free area</strong> - ${fmtMoney(it.SINGLE.FREE_THRESHOLD)}/year before pension reduces</li>
          <li><strong>Couple free area</strong> - ${fmtMoney(it.COUPLE.FREE_THRESHOLD)}/year combined</li>
          <li><strong>Taper rate</strong> - Reduces by 50 cents per dollar over threshold</li>
          <li><strong>Deeming rules</strong> - Financial assets deemed to earn fixed rate regardless of actual returns</li>
        </ul>
        
        <h4>Deeming Rules:</h4>
        <ul>
          <li><strong>Lower threshold</strong> - ${fmtMoney(d.LOW_THRESHOLD.SINGLE)} single, ${fmtMoney(d.LOW_THRESHOLD.COUPLE)} couple (deemed at ${pct(d.LOW_RATE)})</li>
          <li><strong>Above threshold</strong> - Deemed at ${pct(d.HIGH_RATE)}</li>
          <li><strong>Applies to</strong> - Super in pension phase, savings, investments</li>
          <li><strong>Your home</strong> - Not subject to deeming</li>
        </ul>
        
        <h4>Strategy Tips:</h4>
        <ul>
          <li>Lower of asset test or income test applies</li>
          <li>Spend down excess assets if just above threshold</li>
          <li>Consider impact of lump sum withdrawals</li>
          <li>Delay pension application if working part-time</li>
        </ul>
      `;
}

/** HTML block for HelpSystem housing / CRA section. */
export function housingHelpHtml(asAt = new Date()) {
  const f = getAgePensionHelpFacts(asAt);
  const { cra } = f;
  return `
        <h3>Housing in Retirement</h3>
        <p>Your housing situation dramatically affects retirement income needs.</p>
        
        <h4>If You're a Homeowner:</h4>
        <ul>
          <li><strong>Lower expenses</strong> - No rent, but still rates, insurance, maintenance</li>
          <li><strong>Asset test</strong> - Your home is exempt from Age Pension asset test</li>
          <li><strong>Consider downsizing</strong> - Unlock equity, reduce maintenance costs</li>
          <li><strong>Downsizer contribution</strong> - Up to $300,000 into super (age 55+)</li>
        </ul>
        
        <h4>If You're a Renter:</h4>
        <ul>
          <li><strong>Higher expenses</strong> - Rent is typically 20-30% of income</li>
          <li><strong>Rent Assistance</strong> - Singles get up to ${fmtMoney(cra.singleMaxFn, 2)}/fortnight, couples ${fmtMoney(cra.coupleMaxFn, 2)}/fortnight combined</li>
          <li><strong>Asset test advantage</strong> - Higher asset-free threshold (${fmtMoney(f.nonHomeownerExtra)} more for singles)</li>
          <li><strong>Plan for rent increases</strong> - Rent grows faster than inflation</li>
        </ul>
        
        <h4>If You Have a Mortgage:</h4>
        <ul>
          <li><strong>Priority to pay off</strong> - Reduce interest costs, free up cash flow</li>
          <li><strong>Before retirement</strong> - Aim to be mortgage-free by retirement age</li>
          <li><strong>Extra repayments</strong> - Can save years of interest</li>
          <li><strong>Offset accounts</strong> - Use for emergency fund</li>
        </ul>
        
        <h4>Rent Assistance Eligibility:</h4>
        <ul>
          <li>Must be receiving Age Pension (even partial)</li>
          <li>Minimum rent: ${fmtMoney(cra.singleMinRentFn, 0)}/fortnight (singles), ${fmtMoney(cra.coupleMinRentFn, 0)}/fortnight (couples)</li>
          <li>Maximum payment: ${fmtMoney(cra.singleMaxFn, 2)}/fortnight (singles), ${fmtMoney(cra.coupleMaxFn, 2)}/fortnight (couples combined)</li>
          <li>Taper rate: ${Math.round(cra.taperRate * 100)} cents per dollar of rent above minimum</li>
        </ul>
      `;
}
